import React, { Fragment, useEffect, useState } from "react";
import MuiPaper from "Components/MuiPaper/MuiPaper";
import Translator from "Languages/Utility/Translator";
import { satc_accountingHistoryIncTime } from "Constants/TableColumns";
import { useTranslation } from "react-i18next";
import { useSetState } from "Helpers/Hooks";
import MuiAutoComplete from "Components/MuiAutoComplete/MuiAutoComplete";
import Input from "Components/Input/Input";
import MuiButton from "Components/MuiButton/MuiButton";
import Grid from "@material-ui/core/Grid";
import NewIncTime from "./NewIncTime/NewIncTime";
import RemoteTable from "Components/RemoteTable/RemoteTable";
import BackDropLoading from "Components/BackDropLoading2/BackDropLoading";
import { useDispatch, useSelector } from "react-redux";
import { read } from "Helpers/LocalStorage";
import {
  clearAccountingHistoryIncTime,
  getAccountingHistoryIncTime,
  getAccountsAllGroup,
} from "Stores/actions";
import showNotify from "Helpers/Notify";
import PageTitle from "Components/PageTitle_IconCircle/PageTitle";
import * as Icons from "react-bootstrap-icons";

export default function IncTime(row) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [sizePerPage, setSizePerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [tblData, setTblData] = useState([]);
  const [groupId, setGroupId] = useState(-1);
  const [formValid, setFormValid] = useState({});
  const [checkValid, setCheckValid] = useState(false);
  const { accountingIncTimeHistory, allGroups, pagination } = useSelector(
    (state) => ({
      accountingIncTimeHistory: state.saAccounts.accountingIncTimeHistory,
      allGroups: state.saAccounts.allGroups,
      pagination: state.saAccounts.pagination,
    })
  );
  const [controls, setControls] = useSetState({
    description: {
      type: "text",
      value: "",
      validation: null,
    },
    groups: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
    },
  });

  useEffect(() => {
    onClear();
    onEnter();
  }, []);

  const onClear = async () => {
    await dispatch(clearAccountingHistoryIncTime());
    setGroupId(-1);
    setControls({ description: { value: "" } });
  };

  const onEnter = async () => {
    setLoading(true);
    await getAllGroups();
    setLoading(false);
  };

  const getAllGroups = async () => {
    return new Promise(async (resolve) => {
      const params = {
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
      };
      await dispatch(getAccountsAllGroup(params));
      resolve(true);
    });
  };

  useEffect(() => {
    if (allGroups) {
      const selectItems = allGroups.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      setControls({ groups: { selectItems } });
      setControls({ groups: { value: selectItems[0] } });
      setGroupId(selectItems[0].value);
    }
  }, [allGroups]);

  const setValue = (name, value, validation) => {
    setControls({ [name]: { value: value } });
    setFormValid((prev) => ({ ...prev, [name]: validation }));
  };

  const autoCompleteHandler = async (e, value) => {
    const controlName = e.target.id.split("-")[0];
    setControls({ [controlName]: { value: value } });
    setGroupId(value.value);
  };

  const onTableChange = async (type, { page, sizePerPage }) => {
    if (type === "pagination") {
      await search(page, sizePerPage);
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    await search();
    setLoading(false);
  };

  const search = async (_page = 1, _sizePerPage = sizePerPage) => {
    setLoading(true);
    if (groupId === -1) {
      showNotify({ text: t("chooseOneGroup"), customClass: "error" });
    } else {
      const { description } = controls;
      const dataSearch = {
        GroupId: groupId,
        Description: description.value,
      };
      return new Promise(async (resolve) => {
        const params = {
          KeyGroup: read("groupAKey"),
          KeyUser: read("userAKey"),
          ...dataSearch,
        };
        await dispatch(getAccountingHistoryIncTime(params));
        resolve(true);
      });
    }
  };

  useEffect(() => {
    if (accountingIncTimeHistory) {
      setTblData(accountingIncTimeHistory);
      setSizePerPage(pagination.Limit);
      setCurrentPage(pagination.Page);
      setTotal(pagination.Total);
    }
  }, [accountingIncTimeHistory]);

  return (
    <>
      <BackDropLoading loading={loading} />
      <div className="page-top-title-button my-3">
        <PageTitle title={<Translator id="incTime" />} />
        <div className="top-buttons">
          <NewIncTime disabled={loading} onCallBack={handleSearch} />
        </div>
      </div>
      <MuiPaper>
        <Grid container spacing={2} className="mb-2">
          {Object.keys(controls).map((key) => {
            return (
              <Grid item lg={2} md={3} sm={4} xs={12} key={key}>
                {controls[key].type === "select" && (
                  <MuiAutoComplete
                    autoSelect={true}
                    options={controls[key].selectItems}
                    label={<Translator id={key} />}
                    value={controls[key].value}
                    onChange={autoCompleteHandler}
                    id={key}
                  />
                )}
                {controls[key].type === "text" && (
                  <Input
                    setValue={setValue.bind(this)}
                    value={controls[key].value}
                    name={key}
                    validation={controls[key].validation}
                    label={<Translator id={key} />}
                    placeholder={t(key)}
                    checkValid={checkValid}
                  />
                )}
              </Grid>
            );
          })}
          <Grid
            item
            lg={2}
            md={3}
            sm={4}
            xs={12}
            className="margin-top-button no-sm-effect"
          >
            <MuiButton
              color="secondary"
              title={<Translator id="doFilter" />}
              innerIcon={<Icons.Funnel />}
              onClick={handleSearch}
              disabled={loading}
              fullwidthOnSm
            />
          </Grid>
        </Grid>
        <RemoteTable
          data={tblData}
          columns={satc_accountingHistoryIncTime}
          loading={loading}
          page={currentPage}
          sizePerPage={sizePerPage}
          totalSize={total}
          onTableChange={onTableChange}
          title={<Translator id="incTimeTblName" />}
        />
      </MuiPaper>
    </>
  );
}
